/**
 * Structural Tendrils — the connective tissue of the field.
 *
 * Organic curves that flow between cluster regions,
 * creating visible structure without rigid geometry.
 *
 * Architecture:
 *   Trunks — center → category centers (the spine)
 *   Branches — category center → individual clusters (the reach)
 *
 * Think: root systems, neural pathways, river deltas.
 * Not: straight lines, grids, mechanical wiring.
 */

import * as THREE from 'three';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import { useAuraStore } from '../state/store';

const CURVE_SEGMENTS = 48;

export class FieldTendrils {
  public group: THREE.Group;
  private material: THREE.ShaderMaterial;

  constructor(field: SyntheticField) {
    this.group = new THREE.Group();

    // Shared shader for all tendril lines
    this.material = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute float aOpacity;
        attribute vec3 aColor;
        varying float vOpacity;
        varying vec3 vColor;

        void main() {
          vOpacity = aOpacity;
          vColor = aColor;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float u_entryProgress;
        uniform float u_validateReveal;
        varying float vOpacity;
        varying vec3 vColor;

        void main() {
          float baseAlpha = vOpacity * u_entryProgress;
          // Validate mode: structural connections become explicit paths
          float validateAlpha = vOpacity * u_validateReveal * 4.0;
          float alpha = max(baseAlpha, validateAlpha);
          if (alpha < 0.002) discard;
          // In validate, brighten the color slightly for clarity
          vec3 col = mix(vColor, vColor * 1.3 + vec3(0.05), u_validateReveal * 0.5);
          gl_FragColor = vec4(col, min(alpha, 0.5));
        }
      `,
      uniforms: {
        u_entryProgress: { value: 0 },
        u_validateReveal: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    this.buildStructure(field);
  }

  private buildStructure(field: SyntheticField) {
    // Group main clusters by category
    const categories = new Map<string, SyntheticNode[]>();
    for (const node of field.nodes) {
      if (!node.id.startsWith('cluster-')) continue;
      if (!categories.has(node.category)) {
        categories.set(node.category, []);
      }
      categories.get(node.category)!.push(node);
    }

    const origin = new THREE.Vector3(0, 0, 0);

    for (const [, clusters] of categories) {
      if (clusters.length === 0) continue;

      // Category centroid
      const catCenter = new THREE.Vector3();
      for (const c of clusters) {
        catCenter.add(new THREE.Vector3(...c.position));
      }
      catCenter.divideScalar(clusters.length);

      // Category color (brightened)
      const baseColor = new THREE.Color(
        clusters[0].color[0],
        clusters[0].color[1],
        clusters[0].color[2],
      );
      const catColor = new THREE.Color(
        Math.min(1, baseColor.r * 1.4 + 0.1),
        Math.min(1, baseColor.g * 1.4 + 0.1),
        Math.min(1, baseColor.b * 1.4 + 0.1),
      );

      // ── Trunk: center → category center ──
      this.addTendril(origin, catCenter, catColor, 0.10);

      // ── Branches: category center → each cluster ──
      for (const cluster of clusters) {
        const pos = new THREE.Vector3(...cluster.position);
        const branchOpacity = 0.04 + cluster.intensity * 0.06;
        this.addTendril(catCenter, pos, catColor, branchOpacity);
      }

      // ── Cross-links: connect nearby clusters within category ──
      for (let i = 0; i < clusters.length; i++) {
        for (let j = i + 1; j < clusters.length; j++) {
          const a = new THREE.Vector3(...clusters[i].position);
          const b = new THREE.Vector3(...clusters[j].position);
          const dist = a.distanceTo(b);
          if (dist < 180) {
            this.addTendril(a, b, catColor, 0.025);
          }
        }
      }
    }
  }

  /**
   * Create an organic curved line between two points.
   * CatmullRomCurve3 with offset control points for natural flow.
   * Opacity fades at endpoints for organic terminations.
   */
  private addTendril(
    start: THREE.Vector3,
    end: THREE.Vector3,
    color: THREE.Color,
    maxOpacity: number,
  ) {
    const dir = new THREE.Vector3().subVectors(end, start);
    const length = dir.length();
    if (length < 1) return;
    dir.normalize();

    // Perpendicular vector for curvature offset
    const up = new THREE.Vector3(0, 1, 0);
    const perp = new THREE.Vector3().crossVectors(dir, up).normalize();
    if (perp.lengthSq() < 0.01) {
      perp.crossVectors(dir, new THREE.Vector3(1, 0, 0)).normalize();
    }
    const perp2 = new THREE.Vector3().crossVectors(dir, perp).normalize();

    // Deterministic "random" from positions
    const seed = Math.abs(start.x * 13.37 + start.y * 7.13 + end.z * 3.7 + length);
    const s1 = Math.sin(seed) * 0.5 + 0.5;
    const s2 = Math.cos(seed * 1.7) * 0.5 + 0.5;
    const s3 = Math.sin(seed * 2.3) * 0.5 + 0.5;

    const curveAmount = length * 0.18;

    // Two control points with organic offset
    const cp1 = new THREE.Vector3()
      .lerpVectors(start, end, 0.3)
      .addScaledVector(perp, (s1 - 0.5) * curveAmount)
      .addScaledVector(perp2, (s2 - 0.5) * curveAmount * 0.6);

    const cp2 = new THREE.Vector3()
      .lerpVectors(start, end, 0.7)
      .addScaledVector(perp, (s3 - 0.5) * curveAmount * 0.8)
      .addScaledVector(perp2, (s1 - 0.3) * curveAmount * 0.5);

    const curve = new THREE.CatmullRomCurve3(
      [start.clone(), cp1, cp2, end.clone()],
      false,
      'centripetal',
      0.5,
    );
    const points = curve.getPoints(CURVE_SEGMENTS);

    // Build geometry with per-vertex opacity and color
    const positions = new Float32Array(points.length * 3);
    const opacities = new Float32Array(points.length);
    const colors = new Float32Array(points.length * 3);

    for (let i = 0; i < points.length; i++) {
      positions[i * 3] = points[i].x;
      positions[i * 3 + 1] = points[i].y;
      positions[i * 3 + 2] = points[i].z;

      // Fade at endpoints → peak in middle
      const t = i / (points.length - 1);
      const endFade = Math.sin(t * Math.PI);
      opacities[i] = maxOpacity * endFade;

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aOpacity', new THREE.BufferAttribute(opacities, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));

    const line = new THREE.Line(geometry, this.material);
    line.frustumCulled = false;
    this.group.add(line);
  }

  /**
   * Per-frame update. Tendrils appear during the middle of entry.
   */
  update(entryProgress: number) {
    const raw = Math.max(0, (entryProgress - 0.3) / 0.5);
    const eased = Math.min(raw * raw * (3 - 2 * raw), 1); // smoothstep
    this.material.uniforms.u_entryProgress.value = eased;

    // Validate mode: structural connections become explicit influence paths
    const { mode, modeTransitionProgress } = useAuraStore.getState();
    const validateTarget = mode === 'validate' ? modeTransitionProgress : 0;
    const current = this.material.uniforms.u_validateReveal.value as number;
    this.material.uniforms.u_validateReveal.value = current + (validateTarget - current) * 0.06;
  }

  dispose() {
    this.group.traverse((child) => {
      if (child instanceof THREE.Line) {
        child.geometry.dispose();
      }
    });
    this.material.dispose();
  }
}
